import type { Abi, Address } from 'viem';

/**
 * A single contract read to be aggregated into a multicall.
 */
export type BatchRead = {
  address: Address;
  abi: Abi;
  functionName: string;
  args?: readonly unknown[];
};

type MulticallSlot =
  | { status: 'success'; result: unknown }
  | { status: 'failure'; error: unknown };

/**
 * Minimal shape of a viem public client this batcher depends on. Kept narrow so
 * tests can supply a mock exposing only `multicall`, and so both the default
 * public client and the wallet-scoped client structurally satisfy it.
 */
export type MulticallClient = {
  multicall: (params: {
    contracts: readonly BatchRead[];
    allowFailure: true;
  }) => Promise<readonly MulticallSlot[]>;
};

type PendingRead = {
  read: BatchRead;
  resolve: (value: unknown) => void;
  reject: (reason: unknown) => void;
};

// One pending batch per client instance. The default public client and the
// wallet-scoped client are distinct objects, so their reads never mix and a
// client that is torn down is garbage-collected along with its (empty) batch.
const batches = new WeakMap<MulticallClient, PendingRead[]>();

/**
 * Wait window (ms) used to coalesce reads issued within the same polling cycle.
 * A macrotask timer (not a microtask) is required: react-query fires each
 * query's refetch in its own task, so only a timer window groups the reads of
 * a single cycle. 0ms is enough — it flushes right after the current run of due
 * interval callbacks, and is negligible against the 10-15s polling cadence.
 */
const BATCH_WAIT_MS = 0;

/**
 * Enqueue one contract read. Every read enqueued for the same client within one
 * wait window is dispatched as a single `multicall({ allowFailure: true })`, so
 * an N-option market issues ONE batched request per cycle instead of N.
 *
 * Failure isolation: a failing slot rejects only its own promise (the caller's
 * react-query keeps that option's previous value), while the other slots
 * resolve normally. A whole-request failure rejects every pending read.
 */
export function batchedRead<T>(client: MulticallClient, read: BatchRead): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    let queue = batches.get(client);
    if (!queue) {
      queue = [];
      batches.set(client, queue);
      setTimeout(() => flush(client), BATCH_WAIT_MS);
    }
    queue.push({ read, resolve: resolve as (value: unknown) => void, reject });
  });
}

async function flush(client: MulticallClient): Promise<void> {
  const queue = batches.get(client);
  batches.delete(client);
  if (!queue || queue.length === 0) return;

  try {
    const results = await client.multicall({
      contracts: queue.map(item => item.read),
      allowFailure: true,
    });
    queue.forEach((item, index) => {
      const slot = results[index];
      if (slot && slot.status === 'success') {
        item.resolve(slot.result);
      } else {
        // allowFailure isolates this slot: reject only this read so react-query
        // keeps the option's previous value while the others update.
        item.reject(
          slot && slot.status === 'failure'
            ? slot.error
            : new Error('multicall returned no result for slot'),
        );
      }
    });
  } catch (error) {
    // Whole request failed (RPC/network); surface a retryable error to each
    // query so every option keeps its last value and retries on schedule.
    queue.forEach(item => item.reject(error));
  }
}
